import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Button,
  Card,
  DataTable,
  DataTableColumn,
  Modal,
  Select,
  TextInput,
  ToastProvider,
  useToast
} from '../../design-system';
import { portalApi, PortalApiKey, PortalApiKeyCreated } from './portalClient';

const SCOPE_CHOICES = [
  { value: 'read', label: 'read' },
  { value: 'write', label: 'write' },
  { value: 'keys:manage', label: 'keys:manage' },
  { value: 'webhooks:manage', label: 'webhooks:manage' }
];

const Inner: React.FC = () => {
  const { isAuthenticated, getIdToken } = useAuth();
  const { show } = useToast();
  const [loading, setLoading] = React.useState(false);
  const [keys, setKeys] = React.useState<PortalApiKey[]>([]);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createName, setCreateName] = React.useState('');
  const [createEnv, setCreateEnv] = React.useState<'sandbox' | 'production'>('sandbox');
  const [createScopes, setCreateScopes] = React.useState<string[]>(['read']);
  const [createdKey, setCreatedKey] = React.useState<PortalApiKeyCreated | null>(null);
  const [creating, setCreating] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const data = await portalApi.listKeys(getIdToken);
      setKeys(data);
    } catch (err: any) {
      show({ tone: 'error', title: 'Failed to load keys', description: err.message });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getIdToken, show]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async () => {
    if (!createName.trim()) {
      show({ tone: 'error', title: 'Name required' });
      return;
    }
    setCreating(true);
    try {
      const created = await portalApi.createKey(getIdToken, {
        name: createName.trim(),
        environment: createEnv,
        scopes: createScopes
      });
      setCreatedKey(created);
      setCreateOpen(false);
      setCreateName('');
      setCreateScopes(['read']);
      await refresh();
    } catch (err: any) {
      show({ tone: 'error', title: 'Could not create key', description: err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: PortalApiKey) => {
    if (!window.confirm(`Revoke "${key.name}"? Integrations using it will stop working immediately.`)) return;
    try {
      await portalApi.revokeKey(getIdToken, key.keyId);
      show({ tone: 'success', title: 'Key revoked' });
      await refresh();
    } catch (err: any) {
      show({ tone: 'error', title: 'Revoke failed', description: err.message });
    }
  };

  const columns: DataTableColumn<PortalApiKey>[] = [
    { key: 'name', header: 'Name', accessor: (r) => r.name, sortable: true },
    { key: 'env', header: 'Env', accessor: (r) => r.environment, sortable: true },
    { key: 'scopes', header: 'Scopes', accessor: (r) => r.scopes.join(', ') },
    { key: 'createdAt', header: 'Created', accessor: (r) => new Date(r.createdAt).toLocaleString(), sortable: true },
    {
      key: 'active',
      header: 'Status',
      accessor: (r) => (r.isActive ? 'Active' : 'Revoked'),
      sortable: true
    },
    {
      key: 'actions',
      header: '',
      accessor: (r) =>
        r.isActive ? (
          <Button size="sm" variant="ghost" onClick={() => handleRevoke(r)}>
            Revoke
          </Button>
        ) : null,
      align: 'right'
    }
  ];

  if (!isAuthenticated) {
    return (
      <>
        <h1 className="dev-portal__title">API keys</h1>
        <p className="dev-portal__lede">
          Sign in to manage API keys for your enterprise. Unauthenticated
          visitors see the docs only.
        </p>
        <Card>
          <Card.Body>
            <Card.Title>Not signed in</Card.Title>
            <p className="dev-portal__muted" style={{ margin: '8px 0 16px' }}>
              Visit <a href="/login">/login</a> to sign in with your BSO
              credentials, or use the CLI to provision keys from a trusted
              environment.
            </p>
            <pre className="dev-portal__code">{`./infrastructure/public-api/issue-api-key.sh \\
  <enterpriseId> "My Integration" sandbox read`}</pre>
          </Card.Body>
        </Card>
      </>
    );
  }

  return (
    <>
      <h1 className="dev-portal__title">API keys</h1>
      <p className="dev-portal__lede">
        Create and revoke keys for this enterprise. Raw keys are shown once at
        creation — save them in a secret manager immediately.
      </p>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button onClick={() => setCreateOpen(true)} disabled={loading}>
          Issue new key
        </Button>
      </div>

      <DataTable<PortalApiKey>
        columns={columns}
        rows={keys}
        getRowKey={(r) => r.keyId}
        pageSize={10}
        emptyState={loading ? 'Loading…' : 'No keys issued yet.'}
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Issue API key"
        description="Pick a descriptive name, target environment, and scopes. The raw key is shown once."
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={creating}>
              Create key
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TextInput
            label="Key name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            placeholder="Hooters POS integration"
            required
          />
          <Select
            label="Environment"
            value={createEnv}
            onChange={(e) => setCreateEnv(e.target.value as 'sandbox' | 'production')}
            options={[
              { value: 'sandbox', label: 'Sandbox (isolated dataset)' },
              { value: 'production', label: 'Production' }
            ]}
          />
          <fieldset style={{ border: '1px solid var(--bso-border-subtle)', borderRadius: 8, padding: 12 }}>
            <legend style={{ padding: '0 8px', fontSize: 12, color: 'var(--bso-text-secondary)' }}>
              Scopes
            </legend>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {SCOPE_CHOICES.map((s) => (
                <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={createScopes.includes(s.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCreateScopes([...createScopes, s.value]);
                      } else {
                        setCreateScopes(createScopes.filter((x) => x !== s.value));
                      }
                    }}
                  />
                  <code>{s.label}</code>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      </Modal>

      <Modal
        open={createdKey !== null}
        onClose={() => setCreatedKey(null)}
        title="Raw key (save now)"
        description="This is the only time the raw key will be shown."
        size="md"
        footer={<Button onClick={() => setCreatedKey(null)}>Done</Button>}
      >
        {createdKey && (
          <>
            <p style={{ margin: 0, color: 'var(--bso-text-secondary)' }}>
              {createdKey.name} · {createdKey.environment}
            </p>
            <pre className="dev-portal__code" style={{ marginTop: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {createdKey.rawKey}
            </pre>
          </>
        )}
      </Modal>
    </>
  );
};

export const DevKeys: React.FC = () => (
  <ToastProvider>
    <Inner />
  </ToastProvider>
);
